import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

// item 163 — managed classification pick-lists (Job Type, Brand, Runway, …): the
// admin-configured definitions + per-project values. One source for item 162's filters
// + item 168's commission categories.
export interface ClassificationOption {
  id: string;
  label: string;
  sortOrder: number;
}
export interface ClassificationField {
  id: string;
  name: string;
  allowMultiple: boolean;
  sortOrder: number;
  options: ClassificationOption[];
}

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

export const useClassifications = () => {
  const qc = useQueryClient();
  const query = useQuery('classifications', async (): Promise<ClassificationField[]> => {
    const { data } = await axios.get(`${apiUrl()}classification`);
    return data;
  });
  const invalidate = () => qc.invalidateQueries('classifications');

  const saveField = useMutation(
    (f: { id?: string; name: string; allowMultiple: boolean; sortOrder: number }) => axios.post(`${apiUrl()}classification/field`, f),
    { onSuccess: invalidate },
  );
  const archiveField = useMutation((id: string) => axios.delete(`${apiUrl()}classification/field/${id}`), { onSuccess: invalidate });
  const saveOption = useMutation(
    (o: { id?: string; fieldId: string; label: string; sortOrder: number }) => axios.post(`${apiUrl()}classification/option`, o),
    { onSuccess: invalidate },
  );
  const archiveOption = useMutation((id: string) => axios.delete(`${apiUrl()}classification/option/${id}`), { onSuccess: invalidate });

  return {
    fields: query.data ?? [],
    isLoading: query.isLoading,
    saveField: (f: { id?: string; name: string; allowMultiple: boolean; sortOrder: number }) => saveField.mutateAsync(f),
    archiveField: (id: string) => archiveField.mutateAsync(id),
    saveOption: (o: { id?: string; fieldId: string; label: string; sortOrder: number }) => saveOption.mutateAsync(o),
    archiveOption: (id: string) => archiveOption.mutateAsync(id),
  };
};

// per-project assigned option ids
export const useProjectClassifications = (projectId?: string) => {
  const qc = useQueryClient();
  const enabled = !!projectId && projectId !== '00000000-0000-0000-0000-000000000000';
  const query = useQuery(
    ['projectClassifications', projectId],
    async (): Promise<string[]> => {
      const { data } = await axios.get(`${apiUrl()}classification/project/${projectId}`);
      return data;
    },
    { enabled },
  );
  const setForProject = useMutation(
    (optionIds: string[]) => axios.post(`${apiUrl()}classification/project/${projectId}`, optionIds),
    { onSuccess: () => qc.invalidateQueries(['projectClassifications', projectId]) },
  );
  return {
    optionIds: query.data ?? [],
    isLoading: query.isLoading,
    setForProject: (optionIds: string[]) => setForProject.mutateAsync(optionIds),
  };
};
